"use client";
import React, { useEffect, useState, useRef } from "react";
import { gsap } from "gsap";
import { sectionBadge, fadeUp, brutalistBtn } from "../../utils/animationUtils";
import { PiEnvelope, PiLinkedinLogo, PiGithubLogo, PiWhatsappLogo } from 'react-icons/pi';


const socialLinks = [
    { icon: <PiEnvelope weight="bold" size={20} />, label: "Email", href: "mailto:ahmdhzq@gmail.com", desc: "ahmdhzq@gmail.com" },
    { icon: <PiLinkedinLogo weight="bold" size={20} />, label: "LinkedIn", href: "https://www.linkedin.com/in/ahmdhzq18/", desc: "linkedin.com/in/ahmdhzq18" },
    { icon: <PiGithubLogo weight="bold" size={20} />, label: "GitHub", href: "https://github.com/ahmdhzq", desc: "github.com/ahmdhzq" },
    { icon: <PiWhatsappLogo weight="bold" size={20} />, label: "WhatsApp", href: "https://wa.me/6281335280612", desc: "+62 813 3528 0612" },
];

const ContactSection = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const containerRef = useRef(null);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData(event.target);
        const data = Object.fromEntries(formData.entries());

        try {
            const response = await fetch("https://formspree.io/f/meerqqvw", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });
            if (response.ok) {
                alert("Your message has been sent successfully!");
                event.target.reset();
            } else {
                alert("Oops! Something went wrong. Please try again later.");
            }
        } catch (error) {
            console.error("Error submitting form:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.set(['.contact-badge', '.contact-heading', '.contact-submit'], { opacity: 0 });
            gsap.set('.contact-form-item', { opacity: 0, y: 20 });
            gsap.set('.contact-social-link', { opacity: 0, x: 20 });

            sectionBadge('.contact-badge');
            fadeUp('.contact-heading');

            gsap.to('.contact-form-item', {
                opacity: 1, y: 0, stagger: 0.08, duration: 0.5, ease: 'power3.out',
                scrollTrigger: { trigger: '.contact-form-item', start: 'top 88%', toggleActions: 'play none none reverse', fastScrollEnd: true }
            });

            gsap.to('.contact-social-link', {
                opacity: 1, x: 0, stagger: 0.08, duration: 0.5, ease: 'power3.out',
                scrollTrigger: { trigger: '.contact-social-link', start: 'top 88%', toggleActions: 'play none none reverse', fastScrollEnd: true }
            });

            gsap.to('.contact-submit', {
                opacity: 1, duration: 0.4, delay: 0.3,
                scrollTrigger: { trigger: '.contact-submit', start: 'top 88%', toggleActions: 'play none none reverse', fastScrollEnd: true }
            });

            // Form field focus — GSAP driven
            const inputs = document.querySelectorAll('.form-input');
            inputs.forEach(input => {
                input.addEventListener('focus', () => {
                    gsap.to(input, { borderColor: '#FFE600', boxShadow: '4px 4px 0 #111', duration: 0.2, ease: 'power2.out' });
                });
                input.addEventListener('blur', () => {
                    gsap.to(input, { borderColor: '#111', boxShadow: '3px 3px 0 #111', duration: 0.3, ease: 'power3.out' });
                });
            });

            // Social links hover — GSAP (replace CSS transition)
            const socialEls = document.querySelectorAll('.contact-social-link');
            socialEls.forEach(el => {
                const iconBox = el.querySelector('.social-icon-box');
                el.addEventListener('mouseenter', () => {
                    gsap.to(el, { x: 4, y: 4, boxShadow: 'none', duration: 0.15, ease: 'power2.out', overwrite: 'auto' });
                    if (iconBox) gsap.to(iconBox, { backgroundColor: '#111', color: '#FFE600', duration: 0.2, ease: 'power2.out', overwrite: 'auto' });
                });
                el.addEventListener('mouseleave', () => {
                    gsap.to(el, { x: 0, y: 0, boxShadow: '4px 4px 0 #111', ease: 'elastic.out(1,0.5)', duration: 0.5, overwrite: 'auto' });
                    if (iconBox) gsap.to(iconBox, { backgroundColor: '#FFE600', color: '#111', duration: 0.3, ease: 'power3.out', overwrite: 'auto' });
                });
            });

            // Submit button hover — GSAP (replace CSS hover: classes)
            brutalistBtn('.contact-submit');
            const submitEl = document.querySelector('.contact-submit');
            if (submitEl) {
                submitEl.addEventListener('mouseenter', () => {
                    gsap.to(submitEl, { backgroundColor: '#FFE600', color: '#111', duration: 0.2, ease: 'power2.out' });
                });
                submitEl.addEventListener('mouseleave', () => {
                    gsap.to(submitEl, { backgroundColor: '#111', color: '#ffffff', ease: 'power3.out', duration: 0.3 });
                });
            }
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section id="contact" ref={containerRef} className="py-24 font-jakarta bg-[#F2EFE9] overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="mb-14">
                    <div className="contact-badge inline-flex items-center gap-2 px-4 py-2 bg-[#1C1C1E] text-white border-2 border-[#111] rounded-full mb-5"
                        style={{ boxShadow: '3px 3px 0 #111', fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}
                    >
                        <span className="w-2 h-2 rounded-full bg-[#FFE600] inline-block" />
                        CONTACT
                    </div>
                    <h2 className="contact-heading" style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05, fontSize: 'var(--text-h1)', color: 'var(--ink)' }}>
                        Let's Build<br />
                        <span style={{ WebkitTextStroke: '2px #111', color: 'transparent' }}>Something.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    {/* Form Column */}
                    <div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="contact-form-item">
                                <label style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--ink)', display: 'block', marginBottom: 6 }}>Your Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Ahmad Haziq"
                                    required
                                    className="form-input w-full bg-white border-2 border-[#111] text-[#111] placeholder-[#aaa] rounded-lg py-3.5 px-4 focus:outline-none font-medium"
                                    style={{ boxShadow: '3px 3px 0 #111' }}
                                />
                            </div>
                            <div className="contact-form-item">
                                <label style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--ink)', display: 'block', marginBottom: 6 }}>Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="you@example.com"
                                    required
                                    className="form-input w-full bg-white border-2 border-[#111] text-[#111] placeholder-[#aaa] rounded-lg py-3.5 px-4 focus:outline-none font-medium"
                                    style={{ boxShadow: '3px 3px 0 #111' }}
                                />
                            </div>
                            <div className="contact-form-item">
                                <label style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--ink)', display: 'block', marginBottom: 6 }}>Phone (Optional)</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="+62 812 ..."
                                    className="form-input w-full bg-white border-2 border-[#111] text-[#111] placeholder-[#aaa] rounded-lg py-3.5 px-4 focus:outline-none font-medium"
                                    style={{ boxShadow: '3px 3px 0 #111' }}
                                />
                            </div>
                            <div className="contact-form-item">
                                <label style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--ink)', display: 'block', marginBottom: 6 }}>Message</label>
                                <textarea
                                    name="message"
                                    placeholder="Tell me about your project..."
                                    rows="4"
                                    className="form-input w-full bg-white border-2 border-[#111] text-[#111] placeholder-[#aaa] rounded-lg py-3.5 px-4 focus:outline-none font-medium resize-none"
                                    style={{ boxShadow: '3px 3px 0 #111' }}
                                ></textarea>
                            </div>
                            <div className="contact-form-item pt-2">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`contact-submit w-full py-4 px-8 bg-[#111] text-white font-black text-base border-2 border-[#111] rounded-xl shadow-brutal uppercase tracking-wide ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    {isSubmitting ? "Sending..." : "Send Message →"}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Social Links Column */}
                    <div className="flex flex-col justify-start gap-4 lg:pl-8">
                    <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 'var(--text-h3)', color: 'var(--ink)', marginBottom: 8 }}>Find me here too ↓</p>
                        {socialLinks.map((link, i) => (
                            <a
                                key={i}
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                            className="contact-social-link inline-flex items-center gap-4 bg-white border-2 border-[#111] rounded-xl px-5 py-4"
                                style={{ boxShadow: '4px 4px 0 #111', cursor: 'pointer', textDecoration: 'none' }}
                            >
                                <div className="social-icon-box w-9 h-9 rounded-lg bg-[#FFE600] border-2 border-[#111] flex items-center justify-center flex-shrink-0">
                                    {link.icon}
                                </div>
                                <div>
                                    <p className="font-black text-sm text-[#111]">{link.label}</p>
                                    <p className="font-medium text-xs text-[#555] mt-0.5">{link.desc}</p>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
